import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { Box, Button, Paper, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import { BRAND_CORAL, BRAND_CORAL_WASH } from '../constants'

interface Props {
    referenceId: string
    submitterEmail: string
}

const SubmittedScreen = ({ referenceId, submitterEmail }: Props) => {
    const [copied, setCopied] = useState(false)

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(referenceId)
            setCopied(true)
            window.setTimeout(() => setCopied(false), 2000)
        } catch {
            /* clipboard blocked — the ID is on screen to copy by hand */
        }
    }

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 3, sm: 5 },
                textAlign: 'center',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
            }}
        >
            <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main' }} />

            <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                Submission received
            </Typography>

            <Typography sx={{ color: 'text.secondary', mt: 1 }}>
                Thanks — our finance team has everything they need to get started.
            </Typography>

            {referenceId && (
                <Box
                    sx={{
                        mt: 3,
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: BRAND_CORAL_WASH,
                        display: 'inline-block',
                        minWidth: 260,
                    }}
                >
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Your reference ID
                    </Typography>
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Typography
                            sx={{
                                fontWeight: 700,
                                fontFamily: 'monospace',
                                fontSize: '1.1rem',
                                color: BRAND_CORAL,
                            }}
                        >
                            {referenceId}
                        </Typography>
                        <Button
                            size="small"
                            onClick={copy}
                            startIcon={<ContentCopyIcon fontSize="small" />}
                            sx={{ textTransform: 'none', color: 'text.secondary' }}
                        >
                            {copied ? 'Copied' : 'Copy'}
                        </Button>
                    </Stack>
                </Box>
            )}

            {submitterEmail && (
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 3 }}>
                    A summary has been emailed to <strong>{submitterEmail}</strong>.
                </Typography>
            )}

            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 2 }}>
                Please quote your reference ID in any follow-up.
            </Typography>
        </Paper>
    )
}

export default SubmittedScreen
